'use client'
import {
   AiMlAutomation,
   BusinessGrow,
   ContactUsForm,
   FAQs,
   GameChangingIdea,
   TechNServicesHeroSection,
   TemplateSlider,
   WordsOfAppreciation,
} from '@/components'
import { IOSIcon } from '@/svgs'
import React from 'react'
import CommonTechGrid from '../../Components/CommonTechGrid'
import IosFromStrategy from './components/IosFromStrategy'
import IosWhyChoose from './components/IosWhyChoose'

const slides = [
   {
      icon: '/assets/svg/technologies/ios/high.svg',
      topText: 'Native iOS Development',
      header: 'High-Performance iOS Apps',
      details:
         'Build robust, scalable iOS applications using Swift and SwiftUI with optimized UI and seamless functionality.',
   },
   {
      icon: '/assets/svg/technologies/ios/stuning.svg',
      topText: 'UI/UX & Animations',
      header: 'Stunning iOS Interfaces',
      details:
         'Build intuitive, elegant iOS apps using SwiftUI, UIKit, and elegant animations for impactful user experiences.',
   },
   {
      icon: '/assets/svg/technologies/ios/api.svg',
      topText: 'API & Backend Integration',
      header: 'Seamless API Connectivity',
      details:
         'Use cloud services like Firebase and AWS, RESTful APIs, and GraphQL to integrate the backend and sync data in real time.',
   },
   {
      icon: '/assets/svg/technologies/ios/secure.svg',
      topText: 'Security & Data Protection',
      header: 'Secure iOS Applications',
      details:
         'Implement Face ID, Touch ID, encryption, and secure data storage to ensure user privacy and avert cyber attacks.',
   },
   {
      icon: '/assets/svg/technologies/ios/optimzed.svg',
      topText: 'Performance & Optimization',
      header: 'Optimized App Performance',
      details:
         'Enhance speed with Grand Central Dispatch, memory optimization, and efficient resource management.',
   },
   {
      icon: '/assets/svg/technologies/ios/launch.svg',
      topText: 'App Store Deployment',
      header: 'Launch & Scale Apps',
      details:
         'Publish iOS apps on App Store with TestFlight, CI/CD automation, and App Store optimization (ASO).',
   },
]

const gridContent = [
   {
      title: 'Healthcare',
      subtitle: 'iOS Applications for Healthcare Solutions',
      description:
         'Develop secure and HIPAA-compliant healthcare applications for iOS. Build AI-powered medical diagnostics, real-time patient monitoring apps, telemedicine solutions, and health tracking integrations with Apple HealthKit.',
      url: '/assets/svg/technologies/angular/Health.svg',
   },
   {
      title: 'Banking & Finance',
      subtitle: 'iOS Applications for Banking & Finance',
      description:
         'Enhance financial services with iOS-powered applications. Apply secure transactions, AI-based fraud detection, biometric verification (Face ID, Touch ID), and real-time investment monitoring for hassle-free banking experiences.',
      url: '/assets/svg/technologies/angular/Fintech.svg',
   },
   {
      title: 'Restaurant & Hospitality',
      subtitle: 'iOS Applications for Restaurants & Hospitality',
      description:
         'Optimize restaurant management with iOS apps. Create AI-based food ordering apps, reservation management, POS integrations, and loyalty programs for better customer experiences.',
      url: '/assets/svg/technologies/angular/Food.svg',
   },
   {
      title: 'Enhanced eCommerce',
      subtitle: 'iOS Applications for Enhanced eCommerce',
      description:
         'Boost e-commerce businesses with iOS-based applications. We can set up smart product suggestions, hook up Apple Pay so checkout’s faster, keep your inventory updating in real time, and make sure the whole buying process is quick and easy so more people actually finish their orders.',
      url: '/assets/svg/technologies/angular/Ecommerce.svg',
   },
   {
      title: 'Supply Chain & Logistics',
      subtitle: 'iOS Applications for Logistics & Supply Chain',
      description:
         'Streamline supply chain operations with iOS apps. Facilitate real-time shipment tracking, fleet management, AI-based demand forecasting, and automated logistics dashboards for increased efficiency.',
      url: '/assets/svg/technologies/angular/Supply.svg',
   },
   {
      title: 'Social Networking',
      subtitle: 'iOS Applications for Social Networking',
      description:
         'Develop engaging social media platforms with iOS applications. Implement real-time messaging, AI-based content suggestions, live streaming features, and push notifications for user engagement.',
      url: '/assets/svg/technologies/angular/Social.svg',
   },
   {
      title: 'Gaming & Sports Analytics',
      subtitle: 'iOS Applications for Gaming & Sports Analytics',
      description:
         'Enhance gaming experiences with iOS apps. Create AR/VR-facilitated gameplay, real-time leaderboards, player analytics with AI, and instant multiplayer matchmaking for interactive entertainment.',
      url: '/assets/svg/technologies/angular/Gaming.svg',
   },
   {
      title: 'Travel & Tourism',
      subtitle: 'iOS Applications for Travel & Tourism',
      description:
         'Revolutionize travel experiences with iOS applications. Create AI-facilitated itinerary planners, GPS navigation, real-time booking engines, and customized travel recommendations for effortless exploration.',
      url: '/assets/svg/technologies/angular/travel.svg',
   },
   {
      title: 'Real Estate Solutions',
      subtitle: 'iOS Applications for Real Estate',
      description:
         'Optimize real estate platforms with iOS applications. Deploy 3D virtual property tours, AI-facilitated property recommendations, mortgage calculators, and real-time market analysis tools for intelligent property decisions.',
      url: '/assets/svg/technologies/angular/Real-Estate.svg',
   },
   {
      title: 'Education & E-Learning',
      subtitle: 'iOS Applications for Education & E-Learning',
      description:
         'Transform digital learning experiences with iOS apps. Enable AI-powered personalized learning paths, real-time quizzes, offline content access, and interactive video lectures for seamless education.',
      url: '/assets/svg/technologies/angular/Edu.svg',
   },
   {
      title: 'Media & Entertainment',
      subtitle: 'iOS Applications for Media & Entertainment',
      description:
         'Enhance digital media engagement with iOS applications. Implement AI-driven content recommendations, real-time streaming services, interactive AR/VR features, and immersive entertainment experiences.',
      url: '/assets/svg/technologies/angular/Media.svg',
   },
   {
      title: 'Government & Public Sector',
      subtitle: 'iOS Applications for Government & Public Sector',
      description:
         'Modernize public services with iOS applications. Develop secure citizen portals, real-time data tracking, AI-powered government assistance chatbots, and digital identity verification systems for enhanced governance.',
      url: '/assets/svg/technologies/angular/Gov.svg',
   },
]

const faqData = [
   {
      question: 'Why should businesses care about iOS app development?',
      answer:
         'The process of iOS app development is to make apps for Apple smartphones using Swift or Objective-C. Businesses that want to serve premium users must focus on high performance, strong security and using the Apple ecosystem for its benefits.',
   },
   {
      question:
         'How does iOS app development benefit user engagement and brand loyalty?',
      answer:
         'Thanks to a combination of great performance, a consistent user interface and strong integration, iOS apps run smoothly. The result is better user experiences for Apple, more people sticking with it and more trust toward the Apple brand.',
   },
   {
      question:
         'What types of applications are best suited for iOS development?',
      answer:
         'iOS is the best choice for developing mobile banking, e Commerce, healthcare, social media, fitness trackers and apps for companies, thanks to its quick performance and safe data management.',
   },
   {
      question:
         'What are the key advantages of building apps for iOS over other platforms?',
      answer:
         'The iOS system is very safe, it reviews new apps fast, you reach advanced, spending users and you use great tools like Xcode and SwiftUI for development.',
   },
   {
      question: 'Is iOS development suitable for startups and MVPs?',
      answer:
         'Indeed, iOS is suitable for introducing your app to the Apple user base. Swift’s simplicity and the iOS simulator allow for quick iterations and reliable testing before full-scale rollout.',
   },
   {
      question:
         'How secure is iOS for building and deploying mobile applications?',
      answer:
         'Sandboxing, data encryption, securing the boot and biometric authentication are some of the strong security features included with iOS. Apple’s strict guidelines for reviewing apps also benefit the people who develop for the platform.',
   },
   {
      question: 'What is the building time for an iOS application?',
      answer:
         'It takes more time to build more complex apps. A simple iOS app may be completed in 3–5 weeks, but added features and APIs require 2–6 months to finish.',
   },
   {
      question:
         'What tools and technologies are commonly used in iOS development?',
      answer:
         'You will need the Xcode IDE, both Swift and Objective-C languages for programming, SwiftUI or UIKit to create the UI, TestFlight for beta testing and CoreData or Realm for storage of added data.',
   },
   {
      question: 'Can iOS apps be integrated with wearables and smart devices?',
      answer:
         'Apple Watch, HomeKit, HealthKit and various other smart devices are all supported by iOS so that developers can develop applications for fitness, at-home automation and health monitoring.',
   },
   {
      question:
         'What should I look for in an iOS app development company or freelancer?',
      answer:
         'A good choice for your team would be one with tested expertise on Swift and iOS, approved apps in the App Store, familiarity with Apple’s guidelines for user experience and confident knowledge in optimizing security and performance.',
   },
]

function IosPage() {
   return (
      <>
         <TechNServicesHeroSection
            title={`iOS application Development company`}
            subtitle="iOS App Development Services for High-Performance & Scalable Solutions"
            description="Our iOS application development services deliver high-performance, secure, and user-friendly apps. Leveraging Swift, Objective-C, and Apple’s latest frameworks, we create seamless, scalable, and feature-rich iOS solutions—ensuring exceptional user experiences and business growth in a competitive digital marketplace."
            tags={[
               'Custom iOS Mobile Application Development',
               'Scalable and Secure iOS App Solutions',
               'iOS Development',
               'Apple App Solutions',
               'High-Performance and User-Friendly iOS Apps',
            ]}
            HeroIcon={IOSIcon}
         />
         <BusinessGrow />
         <AiMlAutomation
            amdesc="We take advantage of our specialization in building fast, scalable, and feature-filled iOS applications using the latest frameworks. Our dedicated iOS solutions support smooth user interactions, enhanced performance, and adjustability to comply with changing business needs."
            amsubtitle={'Expert iOS Development Solutions'}
            amtitle={'Scalable & Secure iOS Development Services'}
            slides={slides}
         />

         <CommonTechGrid
            gridTitle={'iOS Application-Powered Digital Innovation:'}
            gridSubTitle={'Scalable & Secure Solutions for Every Industry'}
            gridTechbody={'Empowering Businesses with Modern iOS Applications'}
            gridDesc={
               'Developing scalable, user-friendly, and highly effective iOS apps for iPhones, iPads, and Apple Watches is our area of expertise at Benzatine.  Utilizing state-of-the-art technologies such as Swift, Objective-C, SwiftUI, ARKit, and Core ML, our solutions promote digital transformation while guaranteeing flawless performance, enhanced security, and excellent UX/UI.'
            }
            gridContent={gridContent}
         />

         <IosFromStrategy />
         <IosWhyChoose />
         <GameChangingIdea />

         <section className="template-slider-section page_top_section section_secondary_bg">
            <div className="container">
               <h4 className="section_heading_title tech_title our-successfull-section">
                  Our Successful Web Application Development Stories
               </h4>
               <p className="features_description m-auto mb-4">
                  Innovative solutions that transformed businesses and delivered
                  real results.
               </p>
            </div>
            <div>
               <TemplateSlider />
            </div>
         </section>

         <section className="section appreciation-slider-section page_top_section section_secondary_bg pt-0">
            <WordsOfAppreciation />
         </section>

         <FAQs faqsConst={faqData} />

         <ContactUsForm />
      </>
   )
}

export default IosPage
